import { fieldTeamMemberFromIdToken } from "../../../../util/fieldTeamMemberFromIdToken";

const browserLocalStorage = {
  get: (key: string) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  },
};

type WifiScanRow = {
  ssid: string;
  signalStrength: number;
  secured: boolean;
};

function normalizeWifiScanResult(raw: unknown): WifiScanRow[] {
  if (raw == null) return [];

  if (Array.isArray(raw)) {
    const out: WifiScanRow[] = [];
    for (const item of raw) {
      if (typeof item === "string") {
        if (item.length > 0) {
          out.push({ ssid: item, signalStrength: 0, secured: true });
        }
        continue;
      }
      if (typeof item === "object" && item !== null && "ssid" in item) {
        const o = item as Record<string, unknown>;
        const ssid = String(o.ssid ?? "");
        if (!ssid) continue;
        out.push({
          ssid,
          signalStrength:
            typeof o.signalStrength === "number" ? o.signalStrength : 0,
          secured: typeof o.secured === "boolean" ? o.secured : true,
        });
      }
    }
    return out;
  }

  if (typeof raw === "object" && raw !== null && "networks" in raw) {
    const nets = (raw as { networks: unknown }).networks;
    if (!Array.isArray(nets)) return [];
    return normalizeWifiScanResult(nets);
  }

  return [];
}

function mapNativeProvisionResult(
  result: unknown
): Awaited<ReturnType<ICircadiaEngine["provisionDeviceWifi"]>> {
  if (result && typeof result === "object" && "success" in result) {
    return (result as { success: boolean }).success
      ? { status: "connected" }
      : { status: "timeout" };
  }
  if (
    result &&
    typeof result === "object" &&
    "status" in result &&
    typeof (result as { status: unknown }).status === "string"
  ) {
    return result as Awaited<ReturnType<ICircadiaEngine["provisionDeviceWifi"]>>;
  }
  return { status: "timeout" };
}

type FieldTeamLoginResult = Awaited<
  ReturnType<ICircadiaEngine["fieldTeamLogin"]>
>;

const emptyFieldTeamMember = (): FieldTeamLoginResult["member"] => ({
  id: "",
  name: "",
  email: "",
  role: "field_technician",
});

/**
 * Native `window.circadiaapp` (Swift-injected bridge) does not define
 * `fieldTeamLogin`; calling it throws and login shows a generic error.
 * Wrap Cognito event callbacks + `cognitoTokens` into the promise shape
 * Field Setup expects.
 */
function fieldTeamLoginViaNativeCognitoEvents(
  circadia: ICircadiaEngine,
  email: string,
  password: string
): Promise<FieldTeamLoginResult> {
  const c = circadia as ICircadiaEngine & {
    setCognitoLoginDoneCb?: (cb: (() => void) | null) => Promise<unknown>;
    setCognitoLoginFailedCb?: (cb: ((reason: unknown) => void) | null) =>
      | Promise<unknown>
      | unknown;
    cognitoUserLogin?: (u: string, p: string) => void;
    cognitoTokens?: () => Promise<{ AccessToken?: string; IdToken?: string }>;
  };

  const failResult = (): FieldTeamLoginResult => ({
    success: false,
    member: emptyFieldTeamMember(),
    token: "",
  });

  if (typeof c.cognitoUserLogin !== "function") {
    return Promise.resolve(failResult());
  }

  return new Promise((resolve) => {
    let settled = false;
    const cleanup = async () => {
      await Promise.resolve(c.setCognitoLoginDoneCb?.(null));
      await Promise.resolve(c.setCognitoLoginFailedCb?.(null));
    };
    const fail = () => {
      if (settled) return;
      settled = true;
      void cleanup().then(() => {
        resolve(failResult());
      });
    };
    const ok = async () => {
      if (settled) return;
      settled = true;
      try {
        const tokens = await c.cognitoTokens?.();
        const idToken = tokens?.IdToken ?? "";
        const member = idToken
          ? fieldTeamMemberFromIdToken(idToken, email)
          : null;
        await cleanup();
        if (member && idToken) {
          resolve({ success: true, member, token: idToken });
        } else {
          resolve(failResult());
        }
      } catch {
        await cleanup();
        resolve(failResult());
      }
    };

    void (async () => {
      await c.setCognitoLoginDoneCb?.(() => {
        void ok();
      });
      await c.setCognitoLoginFailedCb?.(() => {
        fail();
      });
      c.cognitoUserLogin(email, password);
    })();
  });
}

export const CircadiaEngineProviderFactory: ICircadiaEngineProviderFactory = ({
  circadia,
}) => {
  let gColor1 = "";
  let gColor2 = "";

  const windowCircadiaStatus = !!circadia;
  const isWindowCircadia = () => {
    return windowCircadiaStatus;
  };
  const isIOS = !!circadia && circadia.plat === "ios";

  if (!circadia) {
    circadia = {
      saveConfig: async (key: string, value: string) => {
        return true;
      },
      createSSPin: async (jwt: string, pin: string) => {
        return true;
      },
      changeSSPin: async (jwt: string, pin: string) => {
        return true;
      },
      saveSSValue: async (email: string, key: string, value: string) => {
        const storeValue = {
          email,
          key,
          value,
        };
        try {
          browserLocalStorage.set("saveSSvalue", JSON.stringify(storeValue));
          return true;
        } catch (error) {
          return Promise.reject();
        }
      },
      loadSSValue: async (email: string, key: string, value: string) => {
        const storeValue = {
          email,
          key,
          value,
        };
        const getSSvalue = JSON.parse(
          browserLocalStorage.get("saveSSvalue") || ""
        );
        return storeValue.value === getSSvalue.value ? true : Promise.reject();
      },
      cognitoProvidePin: async (pin: string) => {
        return true;
      },
      setStyleSheet: async (styes: string) => {
        return true;
      },
      setNotifTokenCb: (cb: (token: string) => void) => {
        const token = "dummy-token";
        cb(token);
        return undefined;
      },
      loadSSValueBio: async (email: string, key: string) => {
        return false;
      },
      loginIntercomUser: async (
        token: string,
        hmac: string,
        userInfo: {
          name: string;
          facility: string;
          user_id?: string;
          email?: string;
        }
      ) => {
        let response = null;
        if (circadia) {
          // TODO: remove then or await
          await circadia
            .loginIntercomUser(token, hmac, userInfo)
            .then((res: string) => {
              response = { code: "success", message: res };
            })
            .catch((err: { message: string }) => {
              response = { code: "error", message: err.message };
            });
        }
        return response;
      },
      logoutIntercomUser: async () => {
        let response = null;
        if (circadia) {
          // TODO: remove then or await
          await circadia
            .logoutIntercomUser()
            .then((res: string) => {
              response = { code: "success", message: res };
            })
            .catch((err: { message: string }) => {
              response = { code: "error", message: err.message };
            });
        }
        return response;
      },
      showIntercom: async () => {
        let response = null;
        if (circadia) {
          await circadia
            .showIntercom()
            .then((res: boolean) => {
              response = { code: "success", message: res };
            })
            .catch((err: { message: string }) => {
              response = { code: "error", message: err.message };
            });
        }
        return response;
      },
      hideIntercom: async () => {
        let response = null;
        if (circadia) {
          await circadia
            .hideIntercom()
            .then((res: boolean) => {
              response = { code: "success", message: res };
            })
            .catch((err: { message: string }) => {
              response = { code: "error", message: err.message };
            });
        }
        return response;
      },
      cognitoProvideMfa: (mfa: string) => {},
      cognitoUserLogout: () => {},
      setCognitoLoginFailedCb: (
        cb: (reason: { Code: string; Msg: string }) => void
      ) => {},
      setCognitoLoginDoneCb: (cb: () => void) => {},
      setCognitoMfaRequiredCb: (
        cb: (reason: {
          CODE_DELIVERY_DELIVERY_MEDIUM: string;
          CODE_DELIVERY_DESTINATION: string;
        }) => void
      ) => {},
      setCognitoPinRequiredCb: (cb: () => void) => {},
      cognitoTokens: async () => {
        return { AccessToken: "", IdToken: "" };
      },
      playSound: async () => true,
      cognitoUserLogin: (username: string, password: string) => {},
      cognitoUserEmailOTPLogin: () => {},
      cognitoProvideEmailOTP: () => {},
      setCognitoEmailOTPRequiredCb: () => {},
      cognitoProvideNewPassword: (password: string, attrs?: unknown) => {},
      setCognitoNewPasswordRequiredCb: (cb: (attrs: unknown) => void) => {},
      plat: "web",
      cognitoChangePassword: (
        accessToken: string,
        prevPassword: string,
        newPassword: string
      ) => {
        return { mdg: "", code: 0 };
      },
      intfv: -1,
      sharePdf: async () => {},
      scanDeviceId: async () => {
        throw new Error("scanDeviceId is not available in the browser");
      },
      requestNotifPermission: async () => ({ granted: false }),
      scheduleLocalNotification: async () => {},
      cancelLocalNotification: async () => {},
      getPendingNotificationIds: async () => ({ ids: [] as number[] }),
      getCurrentSsid: async () => ({ ssid: null }),
      saveWifiCredentials: async () => {},
      getWifiCredentials: async () => ({ ssid: null, password: null }),
      clearWifiCredentials: async () => {},
      fieldTeamLogin: async () => ({
        success: false,
        member: {
          id: "",
          name: "",
          email: "",
          role: "field_technician" as const,
        },
        token: "",
      }),
      createPatientAccount: async () => ({
        success: false,
        patientId: "",
      }),
      scanWifiNetworks: async () => [],
      testWifiCredentials: async () => ({ success: false }),
      scanForCircadiaDevice: async () => null,
      provisionDeviceWifi: async () => ({ status: "timeout" as const }),
      openWifiSettings: async () => {},
      isDeviceReachable: async () => ({ reachable: false }),
      pingDevice: async () => ({ reachable: false }),
      checkServerConnection: async () => ({ connected: false }),
      checkMonitoringStatus: async () => ({ active: false }),
      waitForFirstReading: async () => ({ received: false }),
      completeSetup: async () => ({ success: false }),
      escalateSetup: async () => ({ ticketNumber: "" }),
      getTodaysInstalls: async () => [],
      getRecentInstalls: async () => [],
    };
  }

  const playSound = async (sound: string) => {
    if (isWindowCircadia()) {
      return circadia.playSound(sound);
    }
    return true;
  };

  const getDeviceToken = async () =>
    new Promise<string>((resolve) => {
      if (!circadia) {
        return resolve("");
      }

      circadia.setNotifTokenCb((token) => resolve(token));
      return "";
    });

  const changeAppTopAndBottom = (color1: string, color2: string) => {
    if (color1 === gColor1 && color2 === gColor2) return;
    gColor1 = color1;
    gColor2 = color2;

    if (!circadia) {
      return;
    }
    circadia.setStyleSheet(
      `background-color:qlineargradient(spread:pad, x1:0, y1:0, x2:0, y2:1, stop:0.000 ${color1},stop:0.250 ${color1} ,stop:0.750 ${color2}, stop:1.000 ${color2});`
    );
  };

  const loginIntercomUser = async (
    token: string,
    hmac: string,
    userInfo: {
      name: string;
      facility: string;
      user_id?: string;
      email?: string;
    }
  ) => {
    let response = null;
    if (isWindowCircadia()) {
      await circadia
        .loginIntercomUser(token, hmac, userInfo)
        .then((res: string) => {
          response = { code: "success", message: res };
        })
        .catch((err: { message: string }) => {
          response = { code: "error", message: err.message };
        });
    }
    return response;
  };

  const logoutIntercomUser = async () => {
    let response = null;
    if (isWindowCircadia()) {
      await circadia
        .logoutIntercomUser()
        .then((res: string) => {
          response = { code: "success", message: res };
        })
        .catch((err: { message: string }) => {
          response = { code: "error", message: err.message };
        });
    }
    return response;
  };
  const showIntercom = async () => {
    let response = null;
    if (isWindowCircadia()) {
      await circadia
        .showIntercom()
        .then((res: boolean) => {
          response = { code: "success", message: res };
        })
        .catch((err: { message: string }) => {
          response = { code: "error", message: err.message };
        });
    }
    return response;
  };
  const hideIntercom = async () => {
    let response = null;
    if (isWindowCircadia()) {
      await circadia
        .hideIntercom()
        .then((res: boolean) => {
          response = { code: "success", message: res };
        })
        .catch((err: { message: string }) => {
          response = { code: "error", message: err.message };
        });
    }
    return response;
  };

  const scanDeviceId = async (): Promise<{ deviceId: string }> => {
    return circadia.scanDeviceId();
  };

  const scheduleLocalNotification = (
    opts: Parameters<ICircadiaEngine["scheduleLocalNotification"]>[0]
  ): Promise<void> => {
    return circadia.scheduleLocalNotification(opts);
  };

  const cancelLocalNotification = (opts: { id: number }): Promise<void> => {
    return circadia.cancelLocalNotification(opts);
  };

  const getPendingNotificationIds = (): Promise<{ ids: number[] }> => {
    return circadia.getPendingNotificationIds();
  };

  const getCurrentSsid = (): Promise<{ ssid: string | null }> => {
    return circadia.getCurrentSsid();
  };

  const saveWifiCredentials = (options: {
    ssid: string;
    password: string;
  }): Promise<void> => {
    return circadia.saveWifiCredentials(options);
  };

  const getWifiCredentials = (): Promise<{
    ssid: string | null;
    password: string | null;
  }> => {
    return circadia.getWifiCredentials();
  };

  const clearWifiCredentials = (): Promise<void> => {
    return circadia.clearWifiCredentials();
  };

  const fieldTeamLogin = (email: string, password: string) => {
    if (!isWindowCircadia()) {
      return Promise.resolve({
        success: false,
        member: emptyFieldTeamMember(),
        token: "",
      });
    }
    const bridge = circadia as ICircadiaEngine & {
      fieldTeamLogin?: (
        e: string,
        p: string
      ) => Promise<FieldTeamLoginResult>;
    };
    if (typeof bridge.fieldTeamLogin === "function") {
      return bridge.fieldTeamLogin(email, password);
    }
    return fieldTeamLoginViaNativeCognitoEvents(circadia, email, password);
  };

  const createPatientAccount = (
    params: Parameters<ICircadiaEngine["createPatientAccount"]>[0]
  ) => {
    if (isWindowCircadia()) {
      return circadia.createPatientAccount(params);
    }
    return Promise.resolve({ success: false, patientId: "" });
  };

  const scanWifiNetworks = async () => {
    if (!isWindowCircadia()) {
      return [];
    }
    const engine = circadia as ICircadiaEngine;
    try {
      if (typeof engine.scanDeviceWifiNetworks === "function") {
        const raw = await engine.scanDeviceWifiNetworks();
        return normalizeWifiScanResult(raw);
      }
      if (typeof engine.scanWifiNetworks === "function") {
        const raw = await engine.scanWifiNetworks();
        return normalizeWifiScanResult(raw);
      }
    } catch {
      /* fall through */
    }
    return [];
  };

  const testWifiCredentials = (ssid: string, password: string) => {
    if (isWindowCircadia()) {
      return circadia.testWifiCredentials(ssid, password);
    }
    return Promise.resolve({ success: false });
  };

  const isDeviceReachable = () => {
    if (
      isWindowCircadia() &&
      typeof (circadia as ICircadiaEngine).isDeviceReachable === "function"
    ) {
      return (circadia as ICircadiaEngine).isDeviceReachable();
    }
    if (import.meta.env.DEV) {
      return Promise.resolve({ reachable: true });
    }
    return Promise.resolve({ reachable: false });
  };

  const pingDevice = (deviceId: string) => {
    if (isWindowCircadia()) {
      return circadia.pingDevice(deviceId);
    }
    if (import.meta.env.DEV) {
      return Promise.resolve({ reachable: true });
    }
    return Promise.resolve({ reachable: false });
  };

  const checkServerConnection = (deviceId: string) => {
    if (isWindowCircadia()) {
      return circadia.checkServerConnection(deviceId);
    }
    if (import.meta.env.DEV) {
      return Promise.resolve({ connected: true });
    }
    return Promise.resolve({ connected: false });
  };

  const checkMonitoringStatus = (deviceId: string) => {
    if (isWindowCircadia()) {
      return circadia.checkMonitoringStatus(deviceId);
    }
    if (import.meta.env.DEV) {
      return Promise.resolve({ active: true });
    }
    return Promise.resolve({ active: false });
  };

  const waitForFirstReading = (deviceId: string, timeoutMs: number) => {
    if (isWindowCircadia()) {
      return circadia.waitForFirstReading(deviceId, timeoutMs);
    }
    if (import.meta.env.DEV) {
      return Promise.resolve({ received: true });
    }
    return Promise.resolve({ received: false });
  };

  const completeSetup = (
    params: Parameters<ICircadiaEngine["completeSetup"]>[0]
  ) => {
    if (isWindowCircadia()) {
      const fn = (
        circadia as ICircadiaEngine & {
          completeSetup?: (
            p: Parameters<ICircadiaEngine["completeSetup"]>[0]
          ) => ReturnType<ICircadiaEngine["completeSetup"]>;
        }
      ).completeSetup;
      if (typeof fn === "function") {
        return fn.call(circadia, params);
      }
      /* Native bridge often has no `completeSetup`; treat as OK so the web flow can finish */
      return Promise.resolve({ success: true });
    }
    return Promise.resolve({ success: false });
  };

  const escalateSetup = (
    params: Parameters<ICircadiaEngine["escalateSetup"]>[0]
  ) => {
    if (isWindowCircadia()) {
      return circadia.escalateSetup(params);
    }
    return Promise.resolve({ ticketNumber: "" });
  };

  const getTodaysInstalls = () => {
    if (isWindowCircadia()) {
      return circadia.getTodaysInstalls();
    }
    return Promise.resolve([]);
  };

  const getRecentInstalls = (limit: number) => {
    if (isWindowCircadia()) {
      return circadia.getRecentInstalls(limit);
    }
    return Promise.resolve([]);
  };

  const scanForCircadiaDevice = () => {
    if (isWindowCircadia()) {
      return circadia.scanForCircadiaDevice();
    }
    if (import.meta.env.DEV) {
      return new Promise<{
        deviceId: string;
        deviceName: string;
      } | null>((resolve) => {
        setTimeout(
          () =>
            resolve({
              deviceId: "dev-circadia-device",
              deviceName: "Circadia Sensor",
            }),
          2500
        );
      });
    }
    return new Promise<null>((resolve) => {
      setTimeout(() => resolve(null), 30_000);
    });
  };

  const provisionDeviceWifi = (
    params: Parameters<ICircadiaEngine["provisionDeviceWifi"]>[0]
  ) => {
    if (isWindowCircadia()) {
      const fn = (
        circadia as ICircadiaEngine & {
          provisionDeviceWifi?: (...args: unknown[]) => Promise<unknown>;
        }
      ).provisionDeviceWifi;
      if (typeof fn === "function" && fn.length >= 2) {
        const pwd = params.password ?? "";
        return (fn as (ssid: string, password: string) => Promise<unknown>)(
          params.ssid,
          pwd
        )
          .then(mapNativeProvisionResult)
          .catch(() => ({ status: "timeout" as const }));
      }
      return circadia.provisionDeviceWifi(params);
    }
    if (import.meta.env.DEV) {
      return Promise.resolve({ status: "connected" as const });
    }
    return Promise.resolve({ status: "timeout" as const });
  };

  const openWifiSettings = () => {
    if (
      isWindowCircadia() &&
      typeof (circadia as ICircadiaEngine).openWifiSettings === "function"
    ) {
      return (circadia as ICircadiaEngine).openWifiSettings();
    }
    return Promise.resolve();
  };

  return {
    ...circadia,
    changeAppTopAndBottom,
    getDeviceToken,
    hideIntercom,
    showIntercom,
    logoutIntercomUser,
    loginIntercomUser,
    isWindowCircadia,
    playSound,
    scanDeviceId,
    scheduleLocalNotification,
    cancelLocalNotification,
    getPendingNotificationIds,
    getCurrentSsid,
    saveWifiCredentials,
    getWifiCredentials,
    clearWifiCredentials,
    fieldTeamLogin,
    createPatientAccount,
    scanWifiNetworks,
    testWifiCredentials,
    scanForCircadiaDevice,
    provisionDeviceWifi,
    openWifiSettings,
    isDeviceReachable,
    pingDevice,
    checkServerConnection,
    checkMonitoringStatus,
    waitForFirstReading,
    completeSetup,
    escalateSetup,
    getTodaysInstalls,
    getRecentInstalls,
    isIOS,
  };
};
